/* Chọn Trạng thái CS (1 thẻ, lưu qua API) / Thẻ chat (nhiều thẻ, lưu trên máy) ngay trên danh sách và hồ sơ */
import React, { useMemo, useState } from 'react';
import { Input, Popover, message } from 'antd';
import { Check, ChevronDown, Plus, Search } from 'lucide-react';
import type { ChannelCustomerItem } from '../../../api/ChannelCustomer';
import type { CustomerTagItem } from '../../../api/CustomerTag';
import { getNextTags } from '../../../utils/appointmentNextTags';
import { saveCustomerTag } from './api';
import { isDemoId } from './demo';
import { chatTagsOf, shortTag, tagOf, textOn, type ChatTag } from './logic';
import { currentStaffName, useLeadStore } from './store';

export interface PickItem { id: number; name: string; color?: string | null }

/** Chip thẻ (Trạng thái CS / Thẻ chat) theo màu của thẻ */
export const Chip = ({ name, color, title }: { name: string; color?: string | null; title?: string }) => (
  <span title={title ?? name} className="inline-flex max-w-full items-center truncate rounded-md px-2 py-0.5 text-[12px] font-semibold" style={{ background: color || '#64748b', color: textOn(color) }}>{name}</span>
);

/** Danh sách có ô tìm — chọn 1 (đóng ngay) hoặc chọn nhiều (giữ mở) */
function PickList({ items, selected, onToggle, placeholder }: { items: PickItem[]; selected: number[]; onToggle: (it: PickItem) => void; placeholder: string }) {
  const [kw, setKw] = useState('');
  const list = useMemo(() => {
    const k = kw.trim().toLowerCase();
    return k ? items.filter((x) => x.name.toLowerCase().includes(k)) : items;
  }, [items, kw]);
  return (
    <div className="w-[260px]" onClick={(e) => e.stopPropagation()}>
      {items.length > 8 && (
        <Input size="small" allowClear autoFocus prefix={<Search size={13} className="text-gray-400" />} placeholder={placeholder} value={kw} onChange={(e) => setKw(e.target.value)} className="mb-1.5" />
      )}
      <div className="max-h-[300px] overflow-y-auto">
        {list.map((it) => {
          const on = selected.includes(it.id);
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => onToggle(it)}
              className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-white/10 ${on ? 'bg-[#3e91ab]/10' : ''}`}
            >
              <Chip name={it.name} color={it.color} />
              {on && <Check size={15} className="shrink-0 text-[#3e91ab]" />}
            </button>
          );
        })}
        {!list.length && <div className="px-2 py-3 text-center text-[12.5px] text-gray-400">Không có thẻ phù hợp</div>}
      </div>
    </div>
  );
}

const trigger = 'group inline-flex max-w-full items-center gap-1 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-[#3e91ab]/50';

/** Trạng thái CS (customer_tag): bấm chip để đổi. Có ma trận dynamic_next_tags thì chỉ cho chọn thẻ kế tiếp hợp lệ */
export function CsStatusPicker({ r, tags, readOnly }: { r: ChannelCustomerItem; tags: CustomerTagItem[]; readOnly?: boolean }) {
  const l = useLeadStore((s) => s.byId[r.id]);
  const { patch, addTouch } = useLeadStore();
  const [open, setOpen] = useState(false);
  const tag = tagOf(r, l, tags);
  const next = getNextTags(r as any);
  const items: PickItem[] = Array.isArray((r as any).dynamic_next_tags) && next.length
    ? [...(tag ? [tag] : []), ...next.filter((x) => x.id !== tag?.id)]
    : tags;

  const chip = tag
    ? <Chip name={tag.name} color={tag.color} title={tag.local ? `${tag.name} · đổi trên máy này, chờ đồng bộ` : tag.name} />
    : <span className="text-gray-400">Chưa có</span>;
  if (readOnly || !items.length) return chip;

  const pick = async (it: PickItem) => {
    setOpen(false);
    if (it.id === tag?.id) return;
    if (isDemoId(r.id)) patch(r.id, { tagId: it.id });
    else {
      const res = await saveCustomerTag(r.id, it.id);
      if (!res.ok) { message.error(res.message || 'Không đổi được Trạng thái CS'); return; }
    }
    addTouch(r.id, { kind: 'stage', text: `Đổi Trạng thái CS: ${tag ? shortTag(tag.name) : 'chưa có'} → ${shortTag(it.name)}`, by: currentStaffName() });
    message.success(`Đã đổi Trạng thái CS → ${it.name}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} trigger="click" placement="bottomLeft" arrow={false} content={<PickList items={items} selected={tag ? [tag.id] : []} onToggle={pick} placeholder="Tìm trạng thái" />}>
      <button type="button" className={trigger} onClick={(e) => e.stopPropagation()} aria-label="Đổi Trạng thái CS">
        {chip}
        <ChevronDown size={14} className="shrink-0 text-gray-400 group-hover:text-[#3e91ab]" />
      </button>
    </Popover>
  );
}

/** Thẻ chat (care_statuses, nhiều thẻ): bấm để tick / bỏ tick, giữ popup mở để chọn tiếp */
export function ChatTagPicker({ r, all, readOnly }: { r: ChannelCustomerItem; all: PickItem[]; readOnly?: boolean }) {
  const l = useLeadStore((s) => s.byId[r.id]);
  const { patch, addTouch } = useLeadStore();
  const list = chatTagsOf(r, l);

  const chips = list.length
    ? <span className="flex min-w-0 flex-wrap gap-1">{list.map((c) => <Chip key={c.id} name={c.name} color={c.color} />)}</span>
    : <span className="text-gray-400">Chưa gắn</span>;
  if (readOnly || !all.length) return chips;

  const toggle = (it: PickItem) => {
    const on = list.some((x) => x.id === it.id);
    const nextList: ChatTag[] = on ? list.filter((x) => x.id !== it.id) : [...list, { id: it.id, name: it.name, color: it.color }];
    patch(r.id, { chatTags: nextList });
    addTouch(r.id, { kind: 'note', text: `${on ? 'Bỏ' : 'Gắn'} thẻ chat: ${it.name}`, by: currentStaffName() });
  };

  return (
    <Popover trigger="click" placement="bottomLeft" arrow={false} content={<PickList items={all} selected={list.map((x) => x.id)} onToggle={toggle} placeholder="Tìm thẻ chat" />}>
      <button type="button" className={trigger} onClick={(e) => e.stopPropagation()} aria-label="Gắn thẻ chat">
        {chips}
        {list.length
          ? <ChevronDown size={14} className="shrink-0 text-gray-400 group-hover:text-[#3e91ab]" />
          : <Plus size={14} className="shrink-0 text-gray-400 group-hover:text-[#3e91ab]" />}
      </button>
    </Popover>
  );
}
